/** @file Copies an animation with every key under a new name (and an optional client id). */
import { z } from 'zod';
import { currentOf } from '../_shared/currentOf';
import { defineCommand } from '../defineCommand';
import { defineCreate } from '../_shared/defineCreate';
import { animationDraft } from './animationDraft';
import { copyJson } from './copyJson';

const create = defineCreate({
  domain: 'animation',
  kind: 'animation',
  description: 'Copy animation `source` (name or id) with all of its keys under a new `name` (optional client `id`).',
  schema: z.object({ id: z.string().exactOptional(), name: z.string().min(1), source: z.string().min(1) }).strict(),
  build(input, ctx) {
    ctx.resolve('animation', input.source);
    return { name: input.name, bones: {}, slots: {}, ik: {}, deform: {}, drawOrder: [], events: [] };
  },
});

/** animation.duplicate: defineCreate checks id and name; the record is then replaced by a deep copy of the source. */
export const command = defineCommand({
  ...create,
  name: 'animation.duplicate',
  apply(draft, input, ctx) {
    create.apply(draft, input, ctx);
    const id = ctx.created[input.name];
    if (id === undefined) return;
    const source = animationDraft(draft, input.source, ctx).anim;
    draft.animations[id] = { ...copyJson(currentOf(source)), name: input.name };
  },
});
